"use strict";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
Object.defineProperty(exports, "__esModule", { value: true });
exports.handleSignInCallbackRequest = void 0;
const constant_1 = require("../constant");
const utils_1 = require("../utils");
const handleSignInCallbackRequest = async ({ request, handlerInput, userPoolClientId, oAuthConfig, setCookieOptions, origin, }) => {
    const { code, state, error, errorDescription } = (0, utils_1.parseSignInCallbackUrl)(request.url);
    if (errorDescription || error) {
        const searchParamsString = (0, utils_1.createErrorSearchParamsString)({
            error,
            errorDescription,
        });
        return new Response(null, {
            status: 302,
            headers: new Headers({
                location: `${(0, utils_1.getRedirectOrDefault)(handlerInput.redirectOnSignOutComplete)}?${searchParamsString}`,
            }),
        });
    }
    if (!code || !state) {
        return new Response(null, { status: 400 });
    }
    const { [constant_1.PKCE_COOKIE_NAME]: clientPkce, [constant_1.STATE_COOKIE_NAME]: clientState } = (0, utils_1.getCookieValuesFromRequest)(request, [constant_1.PKCE_COOKIE_NAME, constant_1.STATE_COOKIE_NAME]);
    // The state and pkce cookies are removed from cookie store after 5 minutes
    if (!clientState || !clientPkce) {
        const searchParamsString = (0, utils_1.createErrorSearchParamsString)({
            error: constant_1.SIGN_IN_TIMEOUT_ERROR_CODE,
            errorDescription: constant_1.SIGN_IN_TIMEOUT_ERROR_MESSAGE,
        });
        return new Response(null, {
            status: 302,
            headers: new Headers({
                location: `${(0, utils_1.getRedirectOrDefault)(handlerInput.redirectOnSignOutComplete)}?${searchParamsString}`,
            }),
        });
    }
    // Most likely the cookie has been tampered
    if (clientState !== state) {
        return new Response(null, { status: 400 });
    }
    const tokensPayload = await (0, utils_1.exchangeAuthNTokens)({
        redirectUri: (0, utils_1.resolveRedirectSignInUrl)(origin, oAuthConfig),
        userPoolClientId,
        oAuthConfig,
        code,
        codeVerifier: clientPkce,
    });
    if ('error' in tokensPayload) {
        return new Response(tokensPayload.error, { status: 500 });
    }
    const headers = new Headers();
    (0, utils_1.appendSetCookieHeaders)(headers, (0, utils_1.createTokenCookies)({
        tokensPayload,
        userPoolClientId,
    }), (0, utils_1.createTokenCookiesSetOptions)(setCookieOptions, origin));
    (0, utils_1.appendSetCookieHeaders)(headers, (0, utils_1.createSignInFlowProofCookies)({ state: '', pkce: '' }), (0, utils_1.createAuthFlowProofCookiesRemoveOptions)(setCookieOptions));
    // When Cognito redirects back to `/sign-in-callback`, the referer is Cognito
    // endpoint. If redirect end user to `redirectOnSignInComplete` from this point,
    // the referer remains the same.
    // When authN token cookies set as `sameSite: 'strict'`, this may cause the
    // authN tokens cookies set with the redirect response not to be sent to the
    // server. Hence, sending a html page with status 200 to the client, and perform
    // the redirection on the client side.
    headers.set('Content-Type', 'text/html');
    return new Response((0, utils_1.createRedirectionIntermediary)({
        redirectTo: (0, utils_1.getRedirectOrDefault)(handlerInput.redirectOnSignInComplete),
    }), {
        status: 200,
        headers,
    });
};
exports.handleSignInCallbackRequest = handleSignInCallbackRequest;
